import { PDFDocument } from 'pdf-lib';
import { PdfResultViewer } from './pdf-engine.js';
import { mountImageBatch } from './image-batch-queue.js';
import { imageIsAr, imageName, openBitmap } from './image-batch-utils.js';
import { trackEvent } from '../core/analytics.js';
import { saveBlob } from './extra-common.js';

const t = (ar, en) => imageIsAr ? ar : en;
async function addImagePage(pdf, file, report = () => {}) {
  const isJpeg = file.type === 'image/jpeg' || /\.(jpe?g)$/i.test(file.name);
  const isPng = file.type === 'image/png' || /\.png$/i.test(file.name);
  let image = null;

  if (isJpeg) {
    try {
      const bytes = await file.arrayBuffer();
      image = await pdf.embedJpg(bytes);
    } catch {}
  } else if (isPng) {
    try {
      const bytes = await file.arrayBuffer();
      image = await pdf.embedPng(bytes);
    } catch {}
  }

  if (!image) {
    const bitmap = await openBitmap(file);
    const canvas = document.createElement('canvas'); canvas.width = bitmap.width; canvas.height = bitmap.height;
    const context = canvas.getContext('2d', { alpha: file.type === 'image/png' });
    if (file.type !== 'image/png') { context.fillStyle = '#ffffff'; context.fillRect(0, 0, canvas.width, canvas.height); }
    context.drawImage(bitmap, 0, 0); bitmap.close();
    report(55, t('جارٍ تجهيز صفحة PDF', 'Preparing PDF page'));
    const png = file.type === 'image/png';
    const rasterBlob = await new Promise((resolve) => canvas.toBlob(resolve, png ? 'image/png' : 'image/jpeg', 0.92));
    if (!rasterBlob) throw new Error(t('تعذر تحويل الصورة.', 'Could not convert this image.'));
    const bytes = await rasterBlob.arrayBuffer();
    image = png ? await pdf.embedPng(bytes) : await pdf.embedJpg(bytes);
  }
  const factor = Math.min(595 / image.width, 842 / image.height, 1);
  const width = image.width * factor; const height = image.height * factor;
  const page = pdf.addPage([width, height]); page.drawImage(image, { x: 0, y: 0, width, height });
  return { width, height };
}
async function onePage(file, report) {
  const pdf = await PDFDocument.create();
  const dimensions = await addImagePage(pdf, file, report);
  return { blob: new Blob([await pdf.save()], { type: 'application/pdf' }), dimensions };
}

const batch = mountImageBatch({
  rootId: 'img2pdf-app', accept: 'image/jpeg,image/png,image/webp',
  zipName: 'image-pdfs.zip', process: async (file, _settings, report) => {
    report(8, t('جارٍ إنشاء PDF لهذه الصورة', 'Creating a PDF for this image'));
    const { blob, dimensions } = await onePage(file, report);
    report(100, t('اكتمل إنشاء PDF', 'PDF created'));
    return { blob, filename: `${imageName(file.name)}.pdf`, details: `${dimensions.width.toFixed(0)} × ${dimensions.height.toFixed(0)} pt` };
  }
});

let previewDialog = null;
let dialogViewer = null;

function getPreviewDialog() {
  if (previewDialog) return { dialog: previewDialog, viewer: dialogViewer };
  const dialog = document.createElement('dialog');
  dialog.className = 'pdf-modal-dialog';
  dialog.innerHTML = `
    <div class="pdf-result-viewer-card" style="margin:0;border:0;">
      <div class="pdf-viewer-toolbar">
        <div class="pdf-viewer-info">
          <span class="pdf-viewer-title">${t('معاينة المستند الموحّد', 'Combined PDF Preview')}</span>
          <span id="img2pdf-preview-stats" class="pdf-stat-pill">0 pages</span>
        </div>
        <div class="pdf-viewer-nav">
          <button type="button" id="img2pdf-prev" class="btn-secondary btn-sm" disabled>← ${t('السابق', 'Prev')}</button>
          <span class="pdf-page-indicator">${t('صفحة', 'Page')} <strong id="img2pdf-curr">1</strong> ${t('من', 'of')} <span id="img2pdf-total">1</span></span>
          <button type="button" id="img2pdf-next" class="btn-secondary btn-sm">${t('التالي', 'Next')} →</button>
        </div>
        <div class="pdf-viewer-actions">
          <button type="button" id="img2pdf-download" class="btn-primary btn-sm">${t('تنزيل PDF الآن', 'Download PDF Now')}</button>
          <button type="button" id="img2pdf-close" class="btn-secondary btn-sm">${t('إغلاق', 'Close')}</button>
        </div>
      </div>
      <div class="pdf-canvas-container">
        <canvas id="img2pdf-canvas" class="pdf-main-canvas"></canvas>
      </div>
    </div>
  `;
  document.body.appendChild(dialog);
  dialog.querySelector('#img2pdf-close').addEventListener('click', () => dialog.close());

  dialogViewer = new PdfResultViewer({
    canvas: dialog.querySelector('#img2pdf-canvas'),
    prevBtn: dialog.querySelector('#img2pdf-prev'),
    nextBtn: dialog.querySelector('#img2pdf-next'),
    currentNumEl: dialog.querySelector('#img2pdf-curr'),
    totalPagesEl: dialog.querySelector('#img2pdf-total'),
    statsEl: dialog.querySelector('#img2pdf-preview-stats'),
    downloadBtn: dialog.querySelector('#img2pdf-download'),
    isAr: imageIsAr
  });

  previewDialog = dialog;
  return { dialog, viewer: dialogViewer };
}

async function buildCombinedPdfBytes(items) {
  const pdf = await PDFDocument.create();
  for (let i = 0; i < items.length; i++) {
    batch.setStatus(`${t('جارٍ إنشاء الصفحة', 'Creating page')} ${i + 1} / ${items.length}`);
    await addImagePage(pdf, items[i].file);
  }
  return await pdf.save();
}

const previewBtn = document.createElement('button');
previewBtn.type = 'button';
previewBtn.className = 'btn-secondary';
previewBtn.textContent = t('معاينة PDF قبل التنزيل', 'Preview PDF Before Download');
previewBtn.addEventListener('click', async () => {
  const items = batch.getItems();
  if (!items.length || batch.isBusy()) return;
  previewBtn.disabled = true;
  try {
    await batch.runExclusive(async () => {
      try {
        const bytes = await buildCombinedPdfBytes(items);
        const { dialog, viewer } = getPreviewDialog();
        await viewer.loadBytes(bytes, 'kazakan-images-combined.pdf');
        dialog.showModal();
        batch.setStatus(t('المعاينة جاهزة.', 'Preview ready.'));
      } catch (err) {
        batch.setStatus(err.message || t('تعذر إنشاء المعاينة.', 'Could not generate preview.'));
      }
    });
  } finally {
    previewBtn.disabled = false;
  }
});

const combined = document.createElement('button');
combined.type = 'button';
combined.className = 'btn-secondary';
combined.textContent = t('إنشاء PDF واحد من كل الصور', 'Combine all images into one PDF');
combined.addEventListener('click', async () => {
  const items = batch.getItems();
  if (!items.length || batch.isBusy()) return;
  combined.disabled = true;
  try {
    await batch.runExclusive(async () => {
      try {
        const bytes = await buildCombinedPdfBytes(items);
        const blob = new Blob([bytes], { type: 'application/pdf' });
        saveBlob(blob, 'kazakan-images-combined.pdf');
        batch.setStatus(t('تم تنزيل ملف PDF الموحّد.', 'Combined PDF downloaded.'));
        trackEvent('tool_completed', 'images-to-pdf', { pages: items.length, bytes: blob.size });
      } catch (error) {
        batch.setStatus(error.message || t('تعذر إنشاء PDF الموحّد.', 'Could not create the combined PDF.'));
      }
    });
  } finally {
    combined.disabled = false;
  }
});

batch.toolbar.append(previewBtn, combined);

